

<!-- Modern Project Management Dashboard -->
<div class="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50 p-6">
  
  <!-- Header Section -->
  <div class="mb-8">
    <div class="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
      <!-- Title and Description -->
      <div>
        <h1 class="text-3xl font-bold text-gray-900 mb-2">
          {{ "Purchase Order Management" | translate }}
        </h1>
        <p class="text-gray-600">
          Manage and monitor all your construction projects in one place
        </p>
      </div>
      
      <!-- Quick Stats -->
      <div class="flex flex-wrap gap-4">
        <div class="bg-white rounded-lg px-4 py-3 shadow-sm border border-gray-200">
          <div class="flex items-center gap-3">
            <div class="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <svg class="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"></path>
              </svg>
            </div>
            <div>
              <p class="text-sm text-gray-600">Total Projects</p>
              <p class="text-lg font-semibold text-gray-900">{{ dataSource.data.length }}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- Search and Filter Section -->
  <div class="bg-white rounded-2xl shadow-lg border border-gray-200 p-6 mb-8">
    <div class="flex flex-col lg:flex-row gap-4">
      
      <!-- Search Input -->
      <div class="flex-1">
        <div class="relative">
          <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <svg class="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"></path>
            </svg>
          </div>
          <input
            type="text"
            placeholder="Search projects by name, manager, or stage..."
            class="block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-xl leading-5 bg-gray-50 focus:bg-white focus:border-blue-500 focus:ring-2 focus:ring-blue-200 focus:outline-none transition-all duration-200"
            (input)="applyFilter($event)"
          />
        </div>
      </div>

      <!-- Status Filter -->
      <div class="lg:w-64">
        <select
          class="w-full px-4 py-3 border border-gray-300 rounded-xl bg-gray-50 focus:bg-white focus:border-blue-500 focus:ring-2 focus:ring-blue-200 focus:outline-none transition-all duration-200 text-gray-700"
          (change)="filterByStatus($event)"
        >
          <option value="">{{ "ALL_STAGES" | translate }}</option>
          <option value="Pre-Planning">{{ "PRE_PLANNING" | translate }}</option>
          <option value="Planning">{{ "PLANNING" | translate }}</option>
          <option value="Execution">{{ "EXECUTION" | translate }}</option>
          <option value="Closure">{{ "CLOSURE" | translate }}</option>
        </select>
      </div>

      <!-- Action Buttons -->
      <div class="flex flex-col sm:flex-row gap-3">
        <button
          [routerLink]="'/home/add-purchase-order'"
          class="inline-flex items-center px-6 py-3 border border-transparent text-sm font-medium rounded-xl text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-all duration-200 transform hover:scale-105 active:scale-95 shadow-lg"
        >
          <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"></path>
          </svg>
          {{ "ADD_PROJECT" | translate }}
        </button>
        
       
      </div>
    </div>
  </div>

  <!-- Projects Grid/Table -->
  <div class="bg-white rounded-2xl shadow-lg border border-gray-200 overflow-hidden">
    
    <!-- Table Header -->
    <div class="px-6 py-4 border-b border-gray-200 bg-gray-50">
      <h2 class="text-lg font-semibold text-gray-900">Purchase Order Projects Overview</h2>
      <p class="text-sm text-gray-600 mt-1">Manage all your construction projects</p>
    </div>

    <!-- Projects Table -->
    <div class="overflow-x-auto">
      <table mat-table [dataSource]="dataSource" matSort class="w-full">
        
        <!-- Serial Number Column -->
        <ng-container matColumnDef="srNo">
          <th mat-header-cell *matHeaderCellDef class="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">
            #
          </th>
          <td mat-cell *matCellDef="let element; let i = index" class="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
            {{ i + 1 }}
          </td>
        </ng-container>

        <!-- PO Reference Column -->
        <ng-container matColumnDef="poRef">
          <th mat-header-cell *matHeaderCellDef class="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">PO Ref</th>
          <td mat-cell *matCellDef="let element" class="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{{ element.po_ref || '—' }}</td>
        </ng-container>

        <!-- Project Name Column -->
        <ng-container matColumnDef="projectName">
          <th mat-header-cell *matHeaderCellDef mat-sort-header class="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">
            {{ "PROJECT_NAME" | translate }}
          </th>
          <td mat-cell *matCellDef="let element" class="px-6 py-4 whitespace-nowrap">
            <div class="flex items-center">
              <div class="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center mr-3">
                <svg class="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"></path>
                </svg>
              </div>
              <div>
                <div class="text-sm font-medium text-gray-900">{{ element.project_name }}</div>
                <div class="text-sm text-gray-500">Project ID: {{ element.id }}</div>
              </div>
            </div>
          </td>
        </ng-container>

        <ng-container matColumnDef="vendor">
          <th mat-header-cell *matHeaderCellDef class="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">Vendor</th>
          <td mat-cell *matCellDef="let element" class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{{ element.vendor_name || '—' }}</td>
        </ng-container>
        <ng-container matColumnDef="poDate">
          <th mat-header-cell *matHeaderCellDef class="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">PO Date</th>
          <td mat-cell *matCellDef="let element" class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{{ element.po_date | date:'MMM dd, yyyy' }}</td>
        </ng-container>
        <ng-container matColumnDef="poStatus">
          <th mat-header-cell *matHeaderCellDef class="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">Status</th>
          <td mat-cell *matCellDef="let element" class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{{ element.po_status || '—' }}</td>
        </ng-container>
        <ng-container matColumnDef="totalValue">
          <th mat-header-cell *matHeaderCellDef class="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">Total Value</th>
          <td mat-cell *matCellDef="let element" class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{{ element.total_value || '—' }}</td>
        </ng-container>

        <!-- Actions Column -->
        <ng-container matColumnDef="action">
          <th mat-header-cell *matHeaderCellDef class="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">
            {{ "ACTIONS" | translate }}
          </th>
          <td mat-cell *matCellDef="let element" class="px-6 py-4 whitespace-nowrap text-sm font-medium">
            <div class="flex items-center space-x-2">
              
              <!-- Edit Button -->
              <button
                [routerLink]="['/home/edit-purchase-order', element.id]"
                class="inline-flex items-center p-2 border border-transparent rounded-lg text-blue-600 hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors duration-200"
                title="Edit Purchase Order"
              >
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"></path>
                </svg>
              </button>
              <button
                *ngIf="(element.domain || '').toLowerCase() === 'road'"
                [routerLink]="['/home/gis-project', element.project_id || element.project_common_id || element.id]"
                class="inline-flex items-center p-2 border border-transparent rounded-lg text-purple-600 hover:bg-purple-50 focus:outline-none focus:ring-2 focus:ring-purple-500 transition-colors duration-200"
                matTooltip="GIS Editor"
                matTooltipPosition="above"
              >
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                </svg>
              </button>
              <button
                type="button"
                (click)="deleteRecord(element)"
                class="inline-flex items-center p-2 border border-transparent rounded-lg text-red-600 hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-red-500 transition-colors duration-200"
                title="Delete Purchase Order"
              >
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path>
                </svg>
              </button>

            </div>
          </td>
        </ng-container>

        <!-- Table Header and Rows -->
        <tr mat-header-row *matHeaderRowDef="displayedColumns" class="bg-gray-50"></tr>
        <tr mat-row *matRowDef="let row; columns: displayedColumns" class="hover:bg-gray-50 transition-colors duration-150"></tr>
      </table>
    </div>

    <!-- Empty State -->
    <div *ngIf="dataSource.data.length === 0" class="text-center py-12">
      <div class="mx-auto w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mb-4">
        <svg class="w-12 h-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"></path>
        </svg>
      </div>
      <h3 class="text-lg font-medium text-gray-900 mb-2">No projects found</h3>
      <p class="text-gray-500 mb-6">Get started by creating your first project.</p>
      <button
        [routerLink]="'/home/add-purchase-order'"
        class="inline-flex items-center px-6 py-3 border border-transparent text-sm font-medium rounded-xl text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-all duration-200"
      >
        <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"></path>
        </svg>
        Create First Project
      </button>
    </div>

    <!-- Pagination -->
    <div class="px-6 py-4 border-t border-gray-200 bg-gray-50">
      <mat-paginator 
        [length]="dataSource.data.length" 
        [pageSize]="10" 
        [pageSizeOptions]="[5, 10, 25, 50]"
        showFirstLastButtons
        class="bg-transparent"
      >
      </mat-paginator>
    </div>
  </div>
</div>

